import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Footer } from '../common';
import HomeNavBar from '../HomePage/HomeNavBar';
import LeftSidebar from '../HomePage/LeftSidebar';
import FollowersPopup from './FollowersPopup';
import LiveStreamBlock from './LiveStreamBlock';
import MediaGallery from './MediaGallery';
import { ROUTES } from '../../constants';
import ConnectedPlatforms from './ConnectedPlatforms';
import {
  authApi,
  clearStoredAuth,
  readStoredToken,
} from '../../services/authApi';
import { messageApi } from '../../services/messageApi';
import streamApi from '../../services/streamApi';
import communityApi from '../../services/communityApi';
import { readStoredUser, useNavbarUser } from '../../utils/navbarUser';
import './UserProfilePage.css';

const PER_USER_CHATS_ENABLED =
  String(process.env.REACT_APP_ENABLE_PER_USER_CHATS).toLowerCase() === 'true';

function UserProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const navbarUser = useNavbarUser({ notificationCount: 0 });
  const currentUser = readStoredUser();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followHover, setFollowHover] = useState(false);
  const [showFollowersPopup, setShowFollowersPopup] = useState(false);
  const [followersPopupTab, setFollowersPopupTab] = useState('followers');
  const [isHeroPlaying, setIsHeroPlaying] = useState(true);
  const [isHeroMuted, setIsHeroMuted] = useState(true);
  const [isHeroHovering, setIsHeroHovering] = useState(false);
  const heroVideoRef = useRef(null);
  const [liveStatus, setLiveStatus] = useState(null);

  useEffect(() => {
    if (!username) return;

    if (currentUser?.username && currentUser.username === username) {
      navigate(ROUTES.PROFILE, { replace: true });
      return;
    }

    let cancelled = false;

    const fetchProfile = async () => {
      setLoading(true);
      setError(null);

      try {
        const profileResponse = await authApi.getPublicProfile(username);
        if (cancelled) return;

        setProfile(profileResponse.profile || null);

        if (readStoredToken()) {
          try {
            const followResponse = await authApi.checkIsFollowing(username);
            if (!cancelled) {
              setIsFollowing(Boolean(followResponse.isFollowing));
            }
          } catch {
            if (!cancelled) {
              setIsFollowing(false);
            }
          }
        } else if (!cancelled) {
          setIsFollowing(false);
        }
      } catch (fetchError) {
        console.error('Failed to load public profile:', fetchError);
        if (!cancelled) {
          setProfile(null);
          setError(fetchError.message || 'Failed to load profile');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.username, navigate, username]);

  useEffect(() => {
    const profileId = profile?.id;
    if (!profileId) {
      setLiveStatus(null);
      return undefined;
    }

    let cancelled = false;

    const fetchLive = async () => {
      try {
        const statuses = await streamApi.getLiveStatuses([profileId]);
        if (cancelled) return;
        const entry = Array.isArray(statuses)
          ? statuses.find((s) => String(s.user_id) === String(profileId))
          : null;
        setLiveStatus(entry && entry.is_live ? entry : null);
      } catch {
        if (!cancelled) setLiveStatus(null);
      }
    };

    fetchLive();
    const interval = setInterval(fetchLive, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [profile?.id]);

  const handleLogout = useCallback(async () => {
    const token = readStoredToken();
    await authApi.logout(token);
    clearStoredAuth();
    window.location.assign(ROUTES.WELCOME);
  }, []);

  const handleFollowToggle = async () => {
    if (!readStoredToken()) {
      navigate(ROUTES.AUTH);
      return;
    }

    try {
      const response = isFollowing
        ? await authApi.unfollowUser(username)
        : await authApi.followUser(username);

      setIsFollowing(Boolean(response.isFollowing));
      setProfile((current) => (
        current
          ? {
              ...current,
              followersCount: typeof response.followersCount === 'number'
                ? response.followersCount
                : current.followersCount,
            }
          : current
      ));
    } catch (toggleError) {
      console.error('Failed to toggle follow state:', toggleError);
    }
  };

  const handleMessage = async () => {
    if (!readStoredToken()) {
      navigate(ROUTES.AUTH);
      return;
    }

    try {
      const response = await messageApi.createConversation(username);
      navigate(`/messages?conversation=${encodeURIComponent(response.conversationId)}`);
    } catch (messageError) {
      console.error('Failed to start conversation:', messageError);
    }
  };

  const handleJoinChat = async () => {
    if (!readStoredToken()) {
      navigate(ROUTES.AUTH);
      return;
    }
    try {
      await communityApi.joinUserChat(username);
    } catch (joinError) {
      console.error('Failed to join chat:', joinError);
    }
    navigate(`/community/user/${encodeURIComponent(username)}`);
  };

  const openFollowersPopup = (tab) => {
    setFollowersPopupTab(tab);
    setShowFollowersPopup(true);
  };

  if (loading) {
    return (
      <div className="user-profile-page">
        <HomeNavBar
          spadesBalance={navbarUser.spades}
          notificationCount={navbarUser.notifications}
          userAvatar={navbarUser.avatar}
          userName={navbarUser.name}
          onSpadesClick={() => {}}
          onLogout={handleLogout}
          minimal
          forceDocumentNavigation
        />
        <LeftSidebar forceDocumentNavigation />
        <main className="user-profile-page__main">
          <div className="user-profile-page__loader">
            <div className="user-profile-page__loader-ring" />
          </div>
        </main>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="user-profile-page">
        <HomeNavBar
          spadesBalance={navbarUser.spades}
          notificationCount={navbarUser.notifications}
          userAvatar={navbarUser.avatar}
          userName={navbarUser.name}
          onSpadesClick={() => {}}
          onLogout={handleLogout}
          minimal
          forceDocumentNavigation
        />
        <LeftSidebar forceDocumentNavigation />
        <main className="user-profile-page__main">
          <div className="user-profile-page__not-found">
            <div className="user-profile-page__not-found-icon">?</div>
            <h2>User not found</h2>
            <p>{error || "The profile you're looking for doesn't exist."}</p>
            <button type="button" onClick={() => navigate(ROUTES.HOME)}>
              Back to Home
            </button>
          </div>
        </main>
        <Footer fullWidth />
      </div>
    );
  }

  const avatarUrl = profile.profilePictureUrl || profile.oauthProfileImage || null;
  const bannerUrl = profile.bannerUrl || null;
  const heroVideoUrl = profile.videoUrl || null;
  const platforms = profile.connectedPlatforms || [];
  const initial = profile.username ? profile.username.charAt(0).toUpperCase() : '?';
  const isOwnProfile = currentUser?.username && currentUser.username === profile.username;
  const showPublicMedia = profile.mediaVisibility !== 'private' && isFollowing && profile.id;

  return (
    <div className="user-profile-page">
      <HomeNavBar
        spadesBalance={navbarUser.spades}
        notificationCount={navbarUser.notifications}
        userAvatar={navbarUser.avatar}
        userName={navbarUser.name}
        onSpadesClick={() => {}}
        onLogout={handleLogout}
        minimal
        forceDocumentNavigation
      />

      <LeftSidebar forceDocumentNavigation />

      <main className="user-profile-page__main">
        {/* ── Hero: banner background + centered intro video ────────────── */}
        <div
          className="user-profile-page__hero"
          onMouseEnter={() => setIsHeroHovering(true)}
          onMouseLeave={() => setIsHeroHovering(false)}
        >
          {bannerUrl ? (
            <img src={bannerUrl} alt="" className="user-profile-page__hero-banner" />
          ) : (
            <div className="user-profile-page__hero-gradient" />
          )}
          <div className="user-profile-page__hero-overlay" />

          {heroVideoUrl && (
            <div
              className="user-profile-page__hero-video-area"
              onClick={() => {
                if (!heroVideoRef.current) return;
                if (heroVideoRef.current.paused) heroVideoRef.current.play();
                else heroVideoRef.current.pause();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                if (!heroVideoRef.current) return;
                if (heroVideoRef.current.paused) heroVideoRef.current.play();
                else heroVideoRef.current.pause();
              }}
            >
              <div className="user-profile-page__hero-video-container">
                <video
                  ref={heroVideoRef}
                  src={heroVideoUrl}
                  className="user-profile-page__hero-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  onPlay={() => setIsHeroPlaying(true)}
                  onPause={() => setIsHeroPlaying(false)}
                />
                <button
                  type="button"
                  className={`user-profile-page__hero-play-btn${isHeroHovering ? ' user-profile-page__hero-play-btn--visible' : ''}`}
                  aria-label={isHeroPlaying ? 'Pause intro video' : 'Play intro video'}
                  tabIndex={-1}
                >
                  {isHeroPlaying ? (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" rx="1" />
                      <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                  ) : (
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="6 3 20 12 6 21 6 3" />
                    </svg>
                  )}
                </button>
              </div>
              <button
                type="button"
                className={`user-profile-page__hero-mute-btn${isHeroHovering ? ' user-profile-page__hero-mute-btn--visible' : ''}`}
                aria-label={isHeroMuted ? 'Unmute intro video' : 'Mute intro video'}
                tabIndex={-1}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!heroVideoRef.current) return;
                  heroVideoRef.current.muted = !heroVideoRef.current.muted;
                  setIsHeroMuted(heroVideoRef.current.muted);
                }}
              >
                {isHeroMuted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                  </svg>
                )}
              </button>
            </div>
          )}
        </div>

        {/* ── Identity Row: photo left, info right ─────────────────────────── */}
        <div className="user-profile-page__identity-row">
          <div className="user-profile-page__avatar">
            {avatarUrl ? (
              <img src={avatarUrl} alt={profile.username} referrerPolicy="no-referrer" />
            ) : (
              <div className="user-profile-page__avatar-fallback">
                <span>{initial}</span>
              </div>
            )}
          </div>

          <div className="user-profile-page__info">
            <div className="user-profile-page__name-row">
              <h1 className="user-profile-page__username">{profile.username}</h1>
              {profile.isPro && (
                <span className="user-profile-page__badge user-profile-page__badge--pro" title="Discovery Plan PRO">
                  PRO
                </span>
              )}
            </div>
            {profile.joinedDate && (
              <p className="user-profile-page__joined">Joined {profile.joinedDate}</p>
            )}
            <div className="user-profile-page__social-stats">
              <button type="button" className="user-profile-page__stat-btn" onClick={() => openFollowersPopup('followers')}>
                {profile.followersCount || 0} followers
              </button>
              <span className="user-profile-page__stat-sep">•</span>
              <button type="button" className="user-profile-page__stat-btn" onClick={() => openFollowersPopup('following')}>
                {profile.followingCount || 0} following
              </button>
            </div>

            {!isOwnProfile && (
              <div className="user-profile-page__actions">
                <button
                  type="button"
                  className={`user-profile-page__follow-btn${isFollowing ? ' user-profile-page__follow-btn--following' : ''}`}
                  onClick={handleFollowToggle}
                  onMouseEnter={() => setFollowHover(true)}
                  onMouseLeave={() => setFollowHover(false)}
                >
                  {isFollowing ? (followHover ? 'Unfollow' : 'Following') : 'Follow'}
                </button>
                {isFollowing && (
                  <button type="button" className="user-profile-page__action-btn" onClick={handleMessage}>
                    Message
                  </button>
                )}
                {isFollowing && PER_USER_CHATS_ENABLED && (
                  <button type="button" className="user-profile-page__action-btn" onClick={handleJoinChat}>
                    Join Community Chat
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {liveStatus && (
          <LiveStreamBlock
            profile={profile}
            liveStatus={liveStatus}
          />
        )}

        <div className="user-profile-page__content">
          {profile.bio && (
            <p className="user-profile-page__bio">{profile.bio}</p>
          )}

          {platforms.length > 0 && (
            <div className="user-profile-page__platforms-row">
              <ConnectedPlatforms
                accounts={platforms.map((p) => ({
                  platformId: p.platform,
                  username: p.platformUsername || '',
                  profileUrl: p.profileUrl || '',
                }))}
                onAddAccount={() => {}}
                onRemoveAccount={() => {}}
                editMode={false}
              />
            </div>
          )}

          {showPublicMedia && (
            <div className="user-profile-page__media-row">
              <MediaGallery userId={profile.id} isOwnProfile={false} />
            </div>
          )}
        </div>

        <Footer />
      </main>

      <FollowersPopup
        isOpen={showFollowersPopup}
        onClose={() => setShowFollowersPopup(false)}
        username={profile.username}
        initialTab={followersPopupTab}
      />
    </div>
  );
}

export default UserProfilePage;
